import { useState } from 'react'
import { X, Mail, Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { api } from '@/lib/api'

interface SendDigestModalProps {
  isOpen: boolean
  onClose: () => void
}

export function SendDigestModal({ isOpen, onClose }: SendDigestModalProps) {
  const [email, setEmail] = useState('it.support@bluetagus.com')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  if (!isOpen) return null

  const handleSend = async () => {
    setIsLoading(true)
    setResult(null)
    try {
      const response = await api.post('/automation/monitoring/send-report', {
        recipientEmail: email,
      })
      setResult(response.data)
    } catch (err: any) {
      setResult({
        success: false,
        message: err.response?.data?.message || 'Failed to send report',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setResult(null)
    setEmail('it.support@bluetagus.com')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-primary-100">
              <Mail className="h-5 w-5 text-primary-600" />
            </div>
            <h3 className="text-lg font-semibold text-secondary-900">Send Digest Report</h3>
          </div>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-secondary-100 text-secondary-400 hover:text-secondary-600 transition-colors"
            disabled={isLoading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <p className="text-secondary-600 text-sm mb-4">
            Send the current monitoring digest report to the specified email address.
          </p>

          <label htmlFor="digest-email" className="block text-sm font-medium text-secondary-700 mb-1">
            Recipient Email
          </label>
          <input
            id="digest-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            placeholder="email@example.com"
            disabled={isLoading}
          />

          {result && (
            <div className={`mt-4 flex items-start gap-2 p-3 rounded-lg text-sm ${
              result.success
                ? 'bg-green-50 text-green-800'
                : 'bg-red-50 text-red-800'
            }`}>
              {result.success
                ? <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-green-600" />
                : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-600" />
              }
              <span>{result.message}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 pt-0">
          <button
            onClick={handleClose}
            className="btn-secondary"
            disabled={isLoading}
          >
            {result?.success ? 'Close' : 'Cancel'}
          </button>
          {!result?.success && (
            <button
              onClick={handleSend}
              className="btn-primary"
              disabled={isLoading || !email}
            >
              {isLoading ? (
                <span className="flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send Report'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
